import _ from "lodash";
import React, { useState, useRef } from "react";
import classNames from "classnames";
import { Button, Modal, IconButton } from "@toddle-edu/ds-web";
import { useI18n } from "Hooks";
import { useDispatch } from "Hooks/useDispatch";
import { isIdEqual } from "Utils/idHelpers";

import { Popover } from "@toddle-edu/react-tiny-popover";
import { useClickAway } from "react-use";
import MessagingAvatar from "MessagingV2/components/ThreadList/Avatar/Avatar";
import { useHandleScroll } from "Utils/CustomHooks";

import { graphql } from "lib/apolloClient";
import { getOrganizationId, getUserId } from "modules/CommonSelectors";
import { getUserGroupChannelsQuery } from "MessagingV2/modules/MessagingQuery";
import {
  CHAT_TYPE_ENUM,
  MESSAGING_SECTIONS,
  isMessagingGlobalHeaderRevampEnable,
} from "MessagingV2/modules/Utils";

import withLoader from "UIComponents/GqlWithLoader";
import Loading from "UIComponents/Loading";
import { prepareLoadMoreFeed } from "Utils";
import { onEnterOrSpaceKeyDown } from "Utils/AccessibilityHelpers";
import {
  setMessagingSections,
  goToChatIdRoute,
} from "MessagingV2/modules/MessagingModule";

import classes from "./PreviewExistingChannels.scss";
import {
  CaretDownFilled,
  CaretUpFilled,
  TickLargeOutlined,
} from "@toddle-edu/ds-icons";
import ConversationsPreview from "../ConversationsPreview";

const CHANNEL_PAGINATION_COUNT = 20;
const styles = {
  avatarStyles: {
    size: "small",
    shade: "light",
    shape: "square",
    randomizeColor: true,
  },
  modalBodyStyle: {
    overflow: "hidden",
  },
  nextButtonStyle: { height: "48px", padding: "12px 16px" },
};

const ChannelDetails = ({
  channel = {},
  overrideClasses = {},
  onClick,
  isChecked,
}) => {
  const { id, label, profileImageData, type } = channel;

  const { icon, url } = profileImageData || {};

  const isInteractive = _.isFunction(onClick);
  const handleChannelDetailsKeyDown = isInteractive
    ? e => onEnterOrSpaceKeyDown(e, onClick)
    : undefined;

  return (
    <div
      className={classNames(
        classes.channelDetails,
        overrideClasses.channelDetails
      )}
      role={isInteractive ? "button" : undefined}
      tabIndex={isInteractive ? 0 : undefined}
      aria-label={isInteractive ? label : undefined}
      onClick={onClick}
      onKeyDown={handleChannelDetailsKeyDown}
    >
      <div className={classes.channelInfo}>
        <MessagingAvatar
          url={url}
          icon={icon}
          id={id}
          name={label}
          chatType={type}
          customAvatarStyles={styles.avatarStyles}
          customAvatarContainerClassName={classes.messagingAvatarContainer}
        />
        <div className={classNames("text-body-1", classes.channelName)}>
          {label}
        </div>
      </div>
      {isChecked ? <TickLargeOutlined className={classes.tickIcon} /> : null}
    </div>
  );
};

const overrideClasses = {
  channelDetails: classes.overrideChannelDetail,
};

const PopoverOverlayContainer = props => {
  const {
    channels,
    setSelectedChannel,
    hideSelectionOption,
    hasNextPage,
    networkStatus,
    loadMoreFeed,
    selectedId,
  } = props;
  const containerRef = useRef(null);

  useHandleScroll({
    hasNextPage,
    networkStatus,
    loadMoreFeed: loadMoreFeed,
    getScrollRef: () => containerRef.current,
  });

  const isLoadingMore = _.eq(networkStatus, 3);

  return (
    <div className={classes.overlayContainer} ref={containerRef}>
      {_.map(channels, channel => {
        const onClick = () => {
          setSelectedChannel(channel);
          hideSelectionOption();
        };

        const isChecked = isIdEqual(selectedId, channel.id);

        return (
          <ChannelDetails
            channel={channel}
            overrideClasses={overrideClasses}
            onClick={onClick}
            isChecked={isChecked}
          />
        );
      })}
      {isLoadingMore ? <Loading /> : null}
    </div>
  );
};

const ChannelPopoverContainer = props => {
  const {
    channels = [],
    setSelectedChannel,
    isSelectionOpen,
    hideSelectionOption,
    containerRef,
    hasNextPage,
    networkStatus,
    loadMoreFeed,
    selectedId,
  } = props;

  return (
    <Popover
      isOpen={isSelectionOpen}
      positions={["bottom"]}
      reposition={false}
      containerClassName={classes.popoverContainerWrapper}
      containerParent={containerRef?.current}
      content={
        <PopoverOverlayContainer
          channels={channels}
          setSelectedChannel={setSelectedChannel}
          hideSelectionOption={hideSelectionOption}
          hasNextPage={hasNextPage}
          networkStatus={networkStatus}
          loadMoreFeed={loadMoreFeed}
          selectedId={selectedId}
        />
      }
    >
      <div />
    </Popover>
  );
};

const ChannelSelector = ({
  channels = [],
  setSelectedChannel,
  selectedChannel,
  hasNextPage,
  networkStatus,
  loadMoreFeed,
}) => {
  const [isSelectionOpen, setIsSelectionOpen] = useState(false);

  const containerRef = useRef(null);
  const showSelectionOption = () => setIsSelectionOpen(true);
  const hideSelectionOption = () => setIsSelectionOpen(false);

  useClickAway(containerRef, hideSelectionOption);

  return (
    <div className={classes.channelSelectorContainer}>
      <div
        className={classNames(classes.selectedChannelContainer, {
          [classes.selectedChannelContainerOpen]: isSelectionOpen,
        })}
      >
        <ChannelDetails
          channel={selectedChannel}
          onClick={showSelectionOption}
        />
        <IconButton
          variant="plain"
          size="x-small"
          icon={
            isSelectionOpen ? (
              <CaretUpFilled size="small" variant="subtle" />
            ) : (
              <CaretDownFilled size="small" variant="subtle" />
            )
          }
          onClick={showSelectionOption}
        />
      </div>
      <div className={classes.popoverContainer} ref={containerRef}>
        <ChannelPopoverContainer
          channels={channels}
          setSelectedChannel={setSelectedChannel}
          selectedId={_.get(selectedChannel, "id")}
          isSelectionOpen={isSelectionOpen}
          hideSelectionOption={hideSelectionOption}
          containerRef={containerRef}
          hasNextPage={hasNextPage}
          networkStatus={networkStatus}
          loadMoreFeed={loadMoreFeed}
        />
      </div>
    </div>
  );
};

const ChannelSelectorContainer = ({
  channels,
  channelCount,
  setSelectedChannel,
  selectedChannel,
  hasNextPage,
  networkStatus,
  loadMoreFeed,
}) => {
  const { t } = useI18n(["common"]);

  return (
    <div className={classes.channelSelector}>
      <div className={classNames("text-label-3", classes.headerText)}>
        {t("common:count_channel_exists", {
          count: channelCount,
        })}
      </div>
      {_.gt(channelCount, 1) ? (
        <ChannelSelector
          channels={channels}
          setSelectedChannel={setSelectedChannel}
          selectedChannel={selectedChannel}
          hasNextPage={hasNextPage}
          networkStatus={networkStatus}
          loadMoreFeed={loadMoreFeed}
        />
      ) : (
        <ChannelDetails channel={_.first(channels)} />
      )}
    </div>
  );
};

const PreviewExistingChannelModalBody = props => {
  const {
    channels = [],
    channelCount,
    selectedChannel,
    setSelectedChannel,
    hasNextPage,
    networkStatus,
    loadMoreFeed,
  } = props;

  const { t } = useI18n(["common"]);

  if (_.isEmpty(selectedChannel)) {
    setSelectedChannel(_.first(channels));
    return null;
  }

  return (
    <div className={classes.modalBody}>
      <ChannelSelectorContainer
        channels={channels}
        channelCount={channelCount}
        selectedChannel={selectedChannel}
        setSelectedChannel={setSelectedChannel}
        hasNextPage={hasNextPage}
        networkStatus={networkStatus}
        loadMoreFeed={loadMoreFeed}
      />
      <ConversationsPreview
        chatId={_.get(selectedChannel, "id", "")}
        showFromLatest
        headerText={
          _.gt(channelCount, 1)
            ? t("preview_of_selected_channel")
            : t("preview_of_this_channel")
        }
      />
    </div>
  );
};

const ModalBodyWithLoader = withLoader(PreviewExistingChannelModalBody);

const PreviewExistingChannelModal = props => {
  const {
    onClose,
    onBackClick,
    onClickNext,
    channels,
    channelCount,
    hasNextPage,
    networkStatus,
    loadMoreFeed,
    isData,
    isLoading,
  } = props;
  const dispatch = useDispatch();

  const [selectedChannel, setSelectedChannel] = useState({});
  const { t } = useI18n(["common"]);

  const onGoToChannelClick = () => {
    dispatch(setMessagingSections({ section: MESSAGING_SECTIONS.CHANNELS }));
    dispatch(
      goToChatIdRoute({ chatId: _.get(selectedChannel, "id"), type: "push" })
    );
  };

  return (
    <Modal
      height="680px"
      width="700px"
      onClose={onClose}
      isOpen
      footerSeparator
    >
      <Modal.Header
        title={t("common:channel_with_selected_members_already_exists", {
          count: channelCount,
        })}
        subtitle={t("common:use_existing_channel_to_avoid_duplicates", {
          count: channelCount,
        })}
        isClosable
        hasSeparator
      />

      <Modal.Body style={styles.modalBodyStyle}>
        <ModalBodyWithLoader
          channelCount={channelCount}
          selectedChannel={selectedChannel}
          setSelectedChannel={setSelectedChannel}
          channels={channels}
          hasNextPage={hasNextPage}
          networkStatus={networkStatus}
          loadMoreFeed={loadMoreFeed}
          isLoading={isLoading}
          isData={isData}
        />
      </Modal.Body>
      <Modal.Footer hasSeparator>
        <div className={classes.footerContainer}>
          <Button
            variant="inline-progressive"
            size="large"
            onClick={onBackClick}
          >
            {t("common:back")}
          </Button>

          <div className={classes.footerContainerRight}>
            <Button
              variant="outlined-subtle"
              size="large"
              onClick={onClickNext}
            >
              {t("common:continue_anyway")}
            </Button>
            <Button
              variant="progressive"
              size="large"
              onClick={onGoToChannelClick}
              disabled={!selectedChannel}
              {...(isMessagingGlobalHeaderRevampEnable()
                ? {
                    styles: styles.nextButtonStyle,
                    dsVersion: "2.0",
                    shortcut: { code: "Enter", shiftKey: true },
                  }
                : {})}
            >
              {t("common:go_to_channel")}
            </Button>
          </div>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default graphql(getUserGroupChannelsQuery, {
  name: "getUserGroupChannelsQuery",
  options: ({ userIds }) => {
    const exactUserIds = _.uniq([...userIds, getUserId()]);
    return {
      fetchPolicy: "cache-and-network",
      variables: {
        organizationId: getOrganizationId(),
        input: {
          first: CHANNEL_PAGINATION_COUNT,
          type: [CHAT_TYPE_ENUM.CHANNEL_CUSTOM],
          search: {
            exactUserIds,
          },
        },
      },
    };
  },
  props: ({ getUserGroupChannelsQuery }) => {
    const { networkStatus, node } = getUserGroupChannelsQuery;
    const queryVariables = _.get(getUserGroupChannelsQuery, "variables", {});

    const hasNextPage = _.get(node, "chats.pageInfo.hasNextPage", false);
    const endCursor = _.get(node, "chats.pageInfo.endCursor", null);
    const chatEdges = _.get(node, "chats.edges", []);
    const channels = _.map(chatEdges, "node");
    return {
      channels,
      networkStatus,
      hasNextPage,
      isLoading: _.includes([1, 2], networkStatus),
      isData: !_.isEmpty(node),
      loadMoreFeed: prepareLoadMoreFeed({
        variables: { after: endCursor, ...queryVariables },
        queryData: getUserGroupChannelsQuery,
        key: "chats",
      }),
    };
  },
})(PreviewExistingChannelModal);
